// ** React Imports

import CssBaseline from '@mui/material/CssBaseline'
import GlobalStyles from '@mui/material/GlobalStyles'
import { createTheme, responsiveFontSizes, Theme, ThemeProvider } from '@mui/material/styles'
import { ReactNode } from 'react'
// ** Type Imports
import { Settings } from 'src/@core/context/settingsContext'
// ** Theme Config
import themeConfig from 'src/configs/themeConfig'
// ** Direction component for LTR or RTL
import VuexyDirection from 'src/layouts/components/Direction'
import UserThemeOptions from 'src/layouts/UserThemeOptions'
import { deepmerge } from '@/utils/deepmerge'
// ** Global Styles
import GlobalStyling from './globalStyles'
// ** Theme Override Imports
import overrides from './overrides'
// ** Theme
import themeOptions from './ThemeOptions'
import typography from './typography'

interface Props {
  settings: Settings
  children: ReactNode
}

const ThemeComponent = (props: Props) => {
  // ** Props
  const { settings, children } = props

  // ** Merged ThemeOptions of Core and User
  const coreThemeConfig = themeOptions(settings)

  // ** Pass ThemeOptions to CreateTheme Function to create partial theme without component overrides
  let theme = createTheme(coreThemeConfig)

  // ** Deep Merge Component overrides of core and user
  const mergeComponentOverrides = (theme: Theme, settings: Settings) =>
    deepmerge({ ...overrides(theme, settings) }, UserThemeOptions(settings.mode)?.components)

  // ** Deep Merge Typography of core and user
  const mergeTypography = (theme: Theme) => deepmerge(typography(theme), UserThemeOptions(settings.mode)?.typography)

  // ** Continue theme creation and pass merged component overrides to CreateTheme function
  theme = createTheme(theme, {
    components: { ...mergeComponentOverrides(theme, settings) },
    typography: { ...mergeTypography(theme) }
  })

  // ** Set responsive font sizes to true
  if (themeConfig.responsiveFontSizes) {
    theme = responsiveFontSizes(theme)
  }

  return (
    <ThemeProvider theme={theme}>
      <VuexyDirection direction={settings.direction}>
        <CssBaseline />
        <GlobalStyles styles={() => GlobalStyling(theme) as any} />
        {children}
      </VuexyDirection>
    </ThemeProvider>
  )
}

export default ThemeComponent
